import React, { useState, useMemo } from 'react';
import { Search, RefreshCw, Loader2 } from 'lucide-react';

const OpenPositionsTable = ({ positions, isLoading, error, onRefresh, updating }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sideFilter, setSideFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Helper function to get side tag colors
  const getSideTagColor = (side) => {
    const sideUpper = side.toUpperCase();
    if (sideUpper === 'BUY' || sideUpper === 'LONG') {
      return 'bg-green-900/40 text-green-400 border-green-700';
    } else if (sideUpper === 'SELL' || sideUpper === 'SHORT') {
      return 'bg-red-900/40 text-red-400 border-red-700';
    }
    return 'bg-zinc-700 text-zinc-300 border-zinc-600';
  };

  // Helper function to get product type tag colors
  const getProductTypeTagColor = (productType) => {
    const typeUpper = productType.toUpperCase();
    switch (typeUpper) {
      case 'MARGIN':
        return 'bg-purple-900/40 text-purple-400 border-purple-700';
      case 'MIS':
        return 'bg-blue-900/40 text-blue-400 border-blue-700';
      case 'CNC':
        return 'bg-purple-900/40 text-purple-400 border-purple-700';
      case 'NRML':
        return 'bg-orange-900/40 text-orange-400 border-orange-700';
      case 'CO':
        return 'bg-yellow-900/40 text-yellow-400 border-yellow-700';
      case 'BO':
        return 'bg-pink-900/40 text-pink-400 border-pink-700';
      case 'INTRADAY':
        return 'bg-cyan-900/40 text-cyan-400 border-cyan-700';
      case 'DELIVERY':
        return 'bg-emerald-900/40 text-emerald-400 border-emerald-700';
      default:
        return 'bg-zinc-700 text-zinc-300 border-zinc-600';
    }
  };

  // Group positions by symbol and side
  const groupedPositions = useMemo(() => {
    const grouped = new Map();

    positions.forEach((position) => {
      const key = `${position.symbol}-${position.side}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          symbol: position.symbol,
          side: position.side,
          ltp: position.ltp,
          avg_price: position.avg_price,
          product_type: position.product_type,
          pnl: 0,
          quantities: [],
        });
      }

      const group = grouped.get(key);

      // Add to total P&L
      group.pnl += position.pnl;

      // Find or create quantity group
      let qtyGroup = group.quantities.find((q) => q.qty === position.qty);
      if (!qtyGroup) {
        qtyGroup = {
          qty: position.qty,
          clients: [],
        };
        group.quantities.push(qtyGroup);
      }

      // Add client to quantity group
      qtyGroup.clients.push({
        client_id: position.client_id,
        name: position.name,
        pnl: position.pnl,
        position_id: position.position_id,
      });
    });

    // Sort quantities within each group
    grouped.forEach((group) => {
      group.quantities.sort((a, b) => a.qty - b.qty);
    });

    return Array.from(grouped.values());
  }, [positions]);

  // Filter grouped positions based on search term and side filter
  const filteredGroupedPositions = useMemo(() => {
    let filtered = groupedPositions;
    
    // Apply side filter
    if (sideFilter !== 'all') {
      filtered = filtered.filter(group => group.side.toLowerCase() === sideFilter.toLowerCase());
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((group) => {
        const symbolMatch = group.symbol
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
        const clientMatch = group.quantities.some((qty) =>
          qty.clients.some(
            (client) =>
              client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              client.client_id.toLowerCase().includes(searchTerm.toLowerCase())
          )
        );
        return symbolMatch || clientMatch;
      });
    }
    
    return filtered;
  }, [groupedPositions, searchTerm, sideFilter]);

  // Sort filtered positions
  const sortedFilteredPositions = useMemo(() => {
    if (!sortConfig.key) return filteredGroupedPositions;
    
    const sorted = [...filteredGroupedPositions].sort((a, b) => {
      let aValue, bValue;
      
      switch(sortConfig.key) {
        case 'symbol':
          aValue = a.symbol;
          bValue = b.symbol;
          break;
        case 'side':
          aValue = a.side;
          bValue = b.side;
          break;
        case 'ltp':
          aValue = a.ltp;
          bValue = b.ltp;
          break;
        case 'avg_price':
          aValue = a.avg_price;
          bValue = b.avg_price;
          break;
        case 'product_type':
          aValue = a.product_type;
          bValue = b.product_type;
          break;
        case 'pnl':
          aValue = a.pnl;
          bValue = b.pnl;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [filteredGroupedPositions, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return (
        <svg className="w-3 h-3 ml-1 text-zinc-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg className="w-3 h-3 ml-1 text-zinc-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-3 h-3 ml-1 text-zinc-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 bg-zinc-900">
        <Loader2 className="h-10 w-10 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-400 space-y-4 bg-zinc-900">
        <p className="text-center max-w-md">{error}</p>
        <button 
          onClick={onRefresh} 
          className="px-4 py-2 bg-zinc-800 text-zinc-200 rounded-md border border-zinc-700 hover:bg-zinc-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-4 bg-zinc-900 text-zinc-200">
      {/* Header Controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-4 pt-4">
        <h2 className="text-2xl font-bold text-zinc-100">
          Open Positions {`(${sortedFilteredPositions.length})`}
          </h2>

        {/* Search and Refresh */}
        <div className="flex items-center gap-4 w-full md:w-auto">
          {/* Side Filter */}
          <div className="flex gap-2 bg-zinc-800 p-1 rounded-lg border border-zinc-700">
            <button
              onClick={() => setSideFilter('all')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                sideFilter === 'all' 
                  ? 'bg-zinc-700 text-white' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSideFilter('buy')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                sideFilter === 'buy' 
                  ? 'bg-green-900/40 text-green-400 border border-green-700' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Buy
            </button>
            <button
              onClick={() => setSideFilter('sell')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                sideFilter === 'sell' 
                  ? 'bg-red-900/40 text-red-400 border border-red-700' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Sell
            </button>
          </div>

          <div className="relative flex-1 md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
            <input 
              placeholder="Search by Symbol or Client Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-4 py-2 bg-zinc-800 text-zinc-200 rounded-md border border-zinc-700 focus:border-zinc-600 focus:outline-none"
            />
          </div>

          <button 
            onClick={onRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 text-zinc-200 rounded-md border border-zinc-700 hover:bg-zinc-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            {updating === true ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="hidden sm:inline">Update</span>}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-zinc-800 border border-zinc-700 h-[calc(100vh-150px)] overflow-y-auto">
        <div className="">
          <table className="w-full relative">
            <thead className="bg-zinc-700 sticky top-0 h-[40px] z-10">
              <tr>
                <th 
                  onClick={() => handleSort('symbol')}
                  className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider cursor-pointer hover:bg-zinc-600"
                >
                  <div className="flex items-center">
                    Symbol
                    <SortIcon columnKey="symbol" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('side')}
                  className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider cursor-pointer hover:bg-zinc-600"
                >
                  <div className="flex items-center">
                    Side
                    <SortIcon columnKey="side" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('ltp')}
                  className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider cursor-pointer hover:bg-zinc-600"
                >
                  <div className="flex items-center">
                    LTP
                    <SortIcon columnKey="ltp" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('avg_price')}
                  className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider cursor-pointer hover:bg-zinc-600"
                >
                  <div className="flex items-center">
                    Avg Price
                    <SortIcon columnKey="avg_price" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('product_type')}
                  className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider cursor-pointer hover:bg-zinc-600"
                >
                  <div className="flex items-center">
                    Product Type
                    <SortIcon columnKey="product_type" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('pnl')}
                  className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider cursor-pointer hover:bg-zinc-600"
                >
                  <div className="flex items-center">
                    P&L
                    <SortIcon columnKey="pnl" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedFilteredPositions.length > 0 ? (
                sortedFilteredPositions.map((group, index) => (
                  <React.Fragment key={`${group.symbol}-${group.side}-${index}`}>
                    {/* Main row */}
                    <tr className="hover:bg-zinc-750 bg-zinc-800">
                      <td className="px-6 py-4 text-sm font-medium text-zinc-200">
                        {group.symbol}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getSideTagColor(group.side)}`}>
                          {group.side}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-300">
                        ₹{group.ltp.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-300">
                        ₹{group.avg_price.toFixed(2)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getProductTypeTagColor(group.product_type)}`}>
                          {group.product_type}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-sm font-semibold ${
                        group.pnl < 0 ? 'text-red-400' : 'text-green-400'
                      }`}>
                        ₹{group.pnl.toFixed(2)}
                      </td>
                    </tr>

                    {/* Sub-table row for quantities and clients */}
                    <tr className="border-b-2 border-zinc-600">
                      <td colSpan="6" className="p-0 bg-zinc-850">
                        <div className="px-6 py-3 bg-zinc-900/50">
                          <table className="w-full border border-zinc-700 rounded">
                            <thead className="bg-zinc-700">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-zinc-300 uppercase w-24">
                                  Qty
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-zinc-300 uppercase">
                                  Clients
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-zinc-800/50">
                              {group.quantities.map((qtyGroup, qtyIndex) => (
                                <tr key={qtyIndex} className="border-t border-zinc-700">
                                  <td className="px-4 py-3 text-sm font-semibold text-zinc-200 align-top">
                                    {qtyGroup.qty}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex flex-wrap gap-2">
                                      {qtyGroup.clients.map((client, clientIndex) => (
                                        <div 
                                          key={clientIndex} 
                                          className="flex items-center space-x-1 bg-emerald-900/40 text-emerald-400 px-2 py-1 rounded border border-emerald-700 text-xs font-medium"
                                        >
                                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                          <span className="whitespace-nowrap">{client.name}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-zinc-200">
                    No positions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OpenPositionsTable;












































// import React, { useState, useMemo } from 'react';
// import { Search, RefreshCw, Loader2 } from 'lucide-react';

// const OpenPositionsTable = ({ positions, isLoading, error, onRefresh ,updating}) => {
//   const [searchTerm, setSearchTerm] = useState("");

//   // Helper function to get side tag colors
//   const getSideTagColor = (side) => {
//     const sideUpper = side.toUpperCase();
//     if (sideUpper === 'BUY' || sideUpper === 'LONG') {
//       return 'bg-green-900/40 text-green-400 border-green-700';
//     } else if (sideUpper === 'SELL' || sideUpper === 'SHORT') {
//       return 'bg-red-900/40 text-red-400 border-red-700';
//     }
//     return 'bg-zinc-700 text-zinc-300 border-zinc-600';
//   };

//   // Helper function to get product type tag colors
//   const getProductTypeTagColor = (productType) => {
//     const typeUpper = productType.toUpperCase();
//     switch (typeUpper) {
//       case 'MARGIN':
//         return 'bg-purple-900/40 text-purple-400 border-purple-700';
//       case 'MIS':
//         return 'bg-blue-900/40 text-blue-400 border-blue-700';
//       case 'CNC':
//         return 'bg-purple-900/40 text-purple-400 border-purple-700';
//       case 'NRML':
//         return 'bg-orange-900/40 text-orange-400 border-orange-700';
//       case 'CO':
//         return 'bg-yellow-900/40 text-yellow-400 border-yellow-700';
//       case 'BO':
//         return 'bg-pink-900/40 text-pink-400 border-pink-700';
//       case 'INTRADAY':
//         return 'bg-cyan-900/40 text-cyan-400 border-cyan-700';
//       case 'DELIVERY':
//         return 'bg-emerald-900/40 text-emerald-400 border-emerald-700';
//       default:
//         return 'bg-zinc-700 text-zinc-300 border-zinc-600';
//     }
//   };

//   // Group positions by symbol and side
//   const groupedPositions = useMemo(() => {
//     const grouped = new Map();

//     positions.forEach((position) => {
//       const key = `${position.symbol}-${position.side}`;

//       if (!grouped.has(key)) {
//         grouped.set(key, {
//           symbol: position.symbol,
//           side: position.side,
//           ltp: position.ltp,
//           avg_price: position.avg_price,
//           product_type: position.product_type,
//           pnl: 0,
//           quantities: [],
//         });
//       }

//       const group = grouped.get(key);

//       // Add to total P&L
//       group.pnl += position.pnl;

//       // Find or create quantity group
//       let qtyGroup = group.quantities.find((q) => q.qty === position.qty);
//       if (!qtyGroup) {
//         qtyGroup = {
//           qty: position.qty,
//           clients: [],
//         };
//         group.quantities.push(qtyGroup);
//       }

//       // Add client to quantity group
//       qtyGroup.clients.push({
//         client_id: position.client_id,
//         name: position.name,
//         pnl: position.pnl,
//         position_id: position.position_id,
//       });
//     });

//     // Sort quantities within each group
//     grouped.forEach((group) => {
//       group.quantities.sort((a, b) => a.qty - b.qty);
//     });

//     return Array.from(grouped.values());
//   }, [positions]);

//   // Filter grouped positions based on search term
//   const filteredGroupedPositions = useMemo(() => {
//     if (!searchTerm) return groupedPositions;

//     return groupedPositions.filter((group) => {
//       const symbolMatch = group.symbol
//         .toLowerCase()
//         .includes(searchTerm.toLowerCase());
//       const clientMatch = group.quantities.some((qty) =>
//         qty.clients.some(
//           (client) =>
//             client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//             client.client_id.toLowerCase().includes(searchTerm.toLowerCase())
//         )
//       );
//       return symbolMatch || clientMatch;
//     });
//   }, [groupedPositions, searchTerm]);

//   if (isLoading) {
//     return (
//       <div className="flex items-center justify-center h-64 bg-zinc-900">
//         <Loader2 className="h-10 w-10 animate-spin text-zinc-400" />
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex flex-col items-center justify-center h-64 text-red-400 space-y-4 bg-zinc-900">
//         <p className="text-center max-w-md">{error}</p>
//         <button 
//           onClick={onRefresh} 
//           className="px-4 py-2 bg-zinc-800 text-zinc-200 rounded-md border border-zinc-700 hover:bg-zinc-700"
//         >
//           Retry
//         </button>
//       </div>
//     );
//   }

//   return (
//     <div className="flex flex-col space-y-4 bg-zinc-900 text-zinc-200">
//       {/* Header Controls */}
//       <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-4 pt-4">
//         <h2 className="text-2xl font-bold text-zinc-100">Open Positions</h2>

//         {/* Search and Refresh */}
//         <div className="flex items-center gap-4 w-full md:w-auto">
//           <div className="relative flex-1 md:w-80">
//             <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
//             <input 
//               placeholder="Search by Symbol or Client Name..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               className="pl-10 w-full px-4 py-2 bg-zinc-800 text-zinc-200 rounded-md border border-zinc-700 focus:border-zinc-600 focus:outline-none"
//             />
//           </div>

//           <button 
//             onClick={onRefresh}
//             className="flex items-center gap-2 px-4 py-2 bg-zinc-800 text-zinc-200 rounded-md border border-zinc-700 hover:bg-zinc-700 transition-colors"
//           >
//             <RefreshCw className="h-4 w-4" />
//              {updating === true ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="hidden sm:inline">Update</span>}
//           </button>
//         </div>
//       </div>

//       {/* Table */}
//       <div className="bg-zinc-800 border border-zinc-700 h-[calc(100vh-150px)] overflow-y-auto">
//         <div className="">
//           <table className="w-full relative">
//             <thead className="bg-zinc-700 sticky top-0 h-[40px] z-10">
//               <tr>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider">
//                   Symbol
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider">
//                   Side
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider">
//                   LTP
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider">
//                   Avg Price
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider">
//                   Product Type
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider">
//                   P&L
//                 </th>
//               </tr>
//             </thead>
//             <tbody>
//               {filteredGroupedPositions.length > 0 ? (
//                 filteredGroupedPositions.map((group, index) => (
//                   <React.Fragment key={`${group.symbol}-${group.side}-${index}`}>
//                     {/* Main row */}
//                     <tr className=" hover:bg-zinc-750 bg-zinc-800">
//                       <td className="px-6 py-4 text-sm font-medium text-zinc-200">
//                         {group.symbol}
//                       </td>
//                       <td className="px-6 py-4">
//                         <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getSideTagColor(group.side)}`}>
//                           {group.side}
//                         </span>
//                       </td>
//                       <td className="px-6 py-4 text-sm text-zinc-300">
//                         ₹{group.ltp.toFixed(2)}
//                       </td>
//                       <td className="px-6 py-4 text-sm text-zinc-300">
//                         ₹{group.avg_price.toFixed(2)}
//                       </td>
//                       <td className="px-6 py-4">
//                         <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getProductTypeTagColor(group.product_type)}`}>
//                           {group.product_type}
//                         </span>
//                       </td>
//                       <td className={`px-6 py-4 text-sm font-semibold ${
//                         group.pnl < 0 ? 'text-red-400' : 'text-green-400'
//                       }`}>
//                         ₹{group.pnl.toFixed(2)}
//                       </td>
//                     </tr>

//                     {/* Sub-table row for quantities and clients */}
//                     <tr className="border-b-2 border-zinc-600">
//                       <td colSpan="6" className="p-0 bg-zinc-850">
//                         <div className="px-6 py-3 bg-zinc-900/50">
//                           <table className="w-full border border-zinc-700 rounded">
//                             <thead className="bg-zinc-700">
//                               <tr>
//                                 <th className="px-4 py-2 text-left text-xs font-medium text-zinc-300 uppercase w-24">
//                                   Qty
//                                 </th>
//                                 <th className="px-4 py-2 text-left text-xs font-medium text-zinc-300 uppercase">
//                                   Clients
//                                 </th>
//                               </tr>
//                             </thead>
//                             <tbody className="bg-zinc-800/50">
//                               {group.quantities.map((qtyGroup, qtyIndex) => (
//                                 <tr key={qtyIndex} className="border-t border-zinc-700">
//                                   <td className="px-4 py-3 text-sm font-semibold text-zinc-200 align-top">
//                                     {qtyGroup.qty}
//                                   </td>
//                                   <td className="px-4 py-3">
//                                     <div className="flex flex-wrap gap-2">
//                                       {qtyGroup.clients.map((client, clientIndex) => (
//                                         <div 
//                                           key={clientIndex} 
//                                           className="flex items-center space-x-1 bg-emerald-900/40 text-emerald-400 px-2 py-1 rounded border border-emerald-700 text-xs font-medium"
//                                         >
//                                           <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
//                                             <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
//                                           </svg>
//                                           <span className="whitespace-nowrap">{client.name}</span>
//                                         </div>
//                                       ))}
//                                     </div>
//                                   </td>
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </table>
//                         </div>
//                       </td>
//                     </tr>
//                   </React.Fragment>
//                 ))
//               ) : (
//                 <tr>
//                   <td colSpan="6" className="px-6 py-12 text-center text-zinc-200">
//                     No positions found.
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default OpenPositionsTable;

